import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Stat } from '../../../components/ui/stat';
import { Confetti } from '../../../components/ui/confetti';
import { getOrgSavingsSummary, listDomains } from '../../../lib/api';
import { usd } from '../../../lib/graph-visuals';
import { StepScaffold } from '../StepScaffold';

/** Step 5 — the celebration; clicking "Start optimizing" completes onboarding. By
 * now the first sync has converged, so we lead with the actual value found: the
 * estimated monthly savings waiting on the dashboard, with a strip of supporting
 * counts. Confetti only fires when there's a real result to celebrate. */
export function CelebrateStep({
  onDone,
  eyebrow = 'You’re all set',
}: {
  onDone: () => void;
  eyebrow?: string;
}) {
  const savings = useQuery({ queryKey: ['org-savings'], queryFn: getOrgSavingsSummary });
  const domains = useQuery({ queryKey: ['domains'], queryFn: listDomains });

  const totals = savings.data?.totals;
  const currency = savings.data?.currency ?? 'USD';
  const found = totals?.openCount ?? 0;
  const estimated = totals?.estimatedMonthlySaving ?? 0;
  const validated = totals?.validatedCount ?? 0;
  const domainName = domains.data?.[0]?.name;
  const domainCount = savings.data?.domains.length ?? domains.data?.length ?? 1;
  const hasValue = estimated > 0;

  return (
    <>
      <Confetti active={found > 0} onDone={() => {}} />
      <StepScaffold
        eyebrow={eyebrow}
        title={domainName ? `${domainName} is set up` : 'Your domain is set up'}
        lead={
          found > 0
            ? 'Your cloud is mapped and Lizrd already found opportunities to optimize. The highest-impact fixes surface first — each with the exact change and a proven dollar impact.'
            : 'Your cloud is mapped. As the first recommendations land, your biggest savings opportunities surface first — each with the exact fix.'
        }
        footer={
          <Button type="button" onClick={onDone}>
            Start Optimizing
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        }
      >
        {hasValue ? (
          <div className="rounded-md border border-hairline bg-surface-2 p-5">
            <div className="font-display text-5xl font-bold leading-none tracking-tight text-iris tnum">
              {usd(estimated, currency)}
            </div>
            <p className="mt-2 text-sm text-haze">in potential monthly savings identified</p>

            <div className="mt-5 flex divide-x divide-hairline rounded-md border border-hairline bg-surface">
              <div className="flex-1 p-4">
                <Stat value={found} label="opportunities" />
              </div>
              <div className="flex-1 p-4">
                <Stat
                  value={domainCount}
                  label={domainCount === 1 ? 'domain mapped' : 'domains mapped'}
                />
              </div>
              {validated > 0 && (
                <div className="flex-1 p-4">
                  <Stat
                    value={usd(totals?.realizedMonthlySaving ?? 0, currency)}
                    label="proven / mo"
                    accent
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-hairline bg-surface-2 p-4 text-sm text-haze">
            Next up: your domain dashboard, where your biggest savings opportunities surface first —
            each with a copy-paste fix, effort, and rollback.
          </div>
        )}

        <p className="text-xs text-muted">
          Recommendations are ranked by impact — start at the top and work down.
        </p>
      </StepScaffold>
    </>
  );
}
